import React from 'react';
import {createPortal} from 'react-dom';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {Drawer} from 'shared/ui/Drawer';
import type {ProfilePassportDetails, ProfilePassportPhoto} from '../../model/profileDocuments';
import {ProfileDocumentIconView} from '../ProfileDocumentsList/ProfileDocumentIcons';
import styles from './PassportDrawer.module.css';

export type PassportDrawerProps = {
	open: boolean;
	passport: ProfilePassportDetails;
	onClose: () => void;
};

const FIELDS: {key: keyof Omit<ProfilePassportDetails, 'photos'>; label: string}[] = [
	{key: 'seriesNumber', label: 'Серия и номер'},
	{key: 'issuer', label: 'Кем выдан'},
	{key: 'issuedAt', label: 'Дата выдачи'},
	{key: 'departmentCode', label: 'Код подразделения'},
	{key: 'birthDate', label: 'Дата рождения'},
	{key: 'birthPlace', label: 'Место рождения'},
];

export function PassportDrawer({open, passport, onClose}: PassportDrawerProps): React.ReactElement {
	const isMaxSm = useIsMaxSm();
	const [previewPhoto, setPreviewPhoto] = React.useState<ProfilePassportPhoto | null>(null);

	React.useEffect(() => {
		if (!open) {
			setPreviewPhoto(null);
		}
	}, [open]);

	React.useEffect(() => {
		if (!previewPhoto) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				setPreviewPhoto(null);
			}
		};

		document.addEventListener('keydown', onKeyDown, true);

		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [previewPhoto]);

	return (
		<>
			<Drawer
				open={open}
				onClose={onClose}
				showCloseButton
				variant={isMaxSm ? 'fullscreen' : 'side'}
				bodyClassName={styles.drawerBody}
			>
				<div className={styles.hero}>
					<h2 className={styles.title}>Паспорт</h2>
					<span className={styles.heroIcon} aria-hidden>
						<ProfileDocumentIconView name="passport" className={styles.icon} />
					</span>
				</div>

				<div className={styles.scrollContent}>
					{passport.photos.length > 0 ? (
						<section className={styles.photosSection} aria-labelledby="passport-photos-title">
							<p id="passport-photos-title" className={styles.photosLabel}>
								Фото паспорта
							</p>
							<ul className={styles.photos}>
								{passport.photos.map((photo) => (
									<li key={photo.id} className={styles.photoItem}>
										<button
											type="button"
											className={styles.photoButton}
											onClick={() => setPreviewPhoto(photo)}
											aria-label={`Открыть фото: ${photo.label}`}
										>
											<img className={styles.photo} src={photo.url} alt={photo.label} />
										</button>
									</li>
								))}
							</ul>
						</section>
					) : null}

					<div className={styles.fields}>
						{FIELDS.map((field) => (
							<div key={field.key} className={styles.infoBlock}>
								<p className={styles.label}>{field.label}</p>
								<p className={styles.value}>{passport[field.key]}</p>
							</div>
						))}
					</div>
				</div>
			</Drawer>

			{previewPhoto
				? createPortal(
						<div
							className={styles.previewOverlay}
							role="dialog"
							aria-modal="true"
							aria-label={previewPhoto.label}
							onClick={() => setPreviewPhoto(null)}
						>
							<button
								type="button"
								className={styles.previewClose}
								aria-label="Закрыть"
								onClick={() => setPreviewPhoto(null)}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M3.99805 3.99805L20.0004 20.0004" stroke="currentColor" strokeWidth="1.6" />
									<path d="M3.99805 20L20.0004 3.99768" stroke="currentColor" strokeWidth="1.6" />
								</svg>
							</button>
							<img
								className={styles.previewImage}
								src={previewPhoto.url}
								alt={previewPhoto.label}
								onClick={(event) => event.stopPropagation()}
							/>
						</div>,
						document.body,
					)
				: null}
		</>
	);
}
